// src/frontend_react/src/components/ui/Table/Table.js
import React from 'react';
import Typography from '../Typography/Typography';
import Spinner from '../Spinner/Spinner';
import Alert from '../Alert/Alert';
import styles from './Table.module.css';
import Box from '../Box/Box';
import EmptyState from '../EmptyState/EmptyState';

const getNestedValue = (obj, path) => {
  if (typeof path !== 'string' || !path) return undefined;
  return path.split('.').reduce((acc, part) => acc && acc[part] !== undefined ? acc[part] : undefined, obj);
};

const Table = ({
  columns = [],
  data = [],
  isLoading = false,
  loadingMessage = "Loading data...",
  emptyMessage = "No data available.",
  emptyStateTitle,
  emptyStateIcon,
  emptyStateActionText,
  onEmptyStateAction,
  errorMessage,
  title,
  titleSrOnly,
  className,
  tableClassName,
  theadClassName,
  tbodyClassName,
  trClassName,
  thClassName,
  tdClassName,
  containerProps = {},
  children, // For custom row rendering (like our expandable rows)
}) => {

  if (isLoading) {
    return (
      <Box className={`${styles.tableContainer} ${styles.loadingContainer} ${className || ''}`} {...containerProps}>
        <Spinner size="lg" />
        {loadingMessage && <Typography mt="md" color="secondary">{loadingMessage}</Typography>}
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Box className={`${styles.tableContainer} ${className || ''}`} {...containerProps}>
        <Alert variant="error" title="Error Loading Table Data" className={styles.tableMessage}>
          {typeof errorMessage === 'string' ? errorMessage : "An unexpected error occurred."}
        </Alert>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box className={`${styles.tableContainer} ${className || ''}`} {...containerProps}>
        <EmptyState
          title={emptyStateTitle || "No Items Found"}
          message={emptyMessage}
          icon={emptyStateIcon}
          actionText={emptyStateActionText}
          onAction={onEmptyStateAction}
        />
      </Box>
    );
  }

  const renderTableRows = () => {
    return data.map((row, rowIndex) => {
      const rowCells = columns.map((column, colIndex) => {
        const cellValue = column.accessor ? getNestedValue(row, column.accessor) : undefined;
        const cellProps = { row, value: cellValue, data }; // Pass the full original row object to the Cell renderer
        return (
          <td
            key={column.accessor || column.id || column.Header || colIndex}
            className={`${styles.td} ${typeof tdClassName === 'function' ? tdClassName(cellValue, row, rowIndex, colIndex) : (tdClassName || '')}`}
            style={{ width: column.width, ...column.tdStyle }}
          >
            {column.Cell ? column.Cell(cellProps) : (cellValue !== undefined && cellValue !== null ? String(cellValue) : '')}
          </td>
        );
      });

      if (typeof children === 'function') {
        // If a render prop is provided, use it. It is responsible for rendering the <tr> and its content.
        return children({ row, children: rowCells, rowIndex });
      }

      // Default rendering if no render prop is used
      return (
        <tr key={row.id || rowIndex} className={`${styles.tr} ${typeof trClassName === 'function' ? trClassName(row, rowIndex) : (trClassName || '')}`}>
          {rowCells}
        </tr>
      );
    });
  };

  return (
    <Box className={`${styles.tableContainer} ${className || ''}`} {...containerProps}>
      {title && <Typography variant="h3" component="h2" p="md" borderBottom="1px solid var(--cipher-color-border-subtle)" mb="0" className={titleSrOnly ? 'sr-only' : undefined}>{title}</Typography>}
      <div className={styles.tableWrapperForScroll}>
        <table className={`${styles.table} ${tableClassName || ''}`}>
          <thead className={`${styles.thead} ${theadClassName || ''}`}>
            <tr>
              {columns.map((column, colIndex) => (
                <th key={column.accessor || column.id || column.Header || colIndex} className={`${styles.th} ${typeof thClassName === 'function' ? thClassName(column, colIndex) : (thClassName || '')}`} style={{ width: column.width ? column.width : undefined, ...column.thStyle }}>
                  {column.Header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={`${styles.tbody} ${tbodyClassName || ''}`}>
            {renderTableRows()}
          </tbody>
        </table>
      </div>
    </Box>
  );
};

export default Table;